import { FC, useEffect, useRef, useState } from "react";

import { Button, toast, ToastAction } from "@upstageai/ui";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";

import { handleKeyPress } from "../atom/function/parsingTablekeyBoardManger";
import ErrorDialog from "../components/Dialog/ErrorDialog";
import NonGroupingAddDialog from "../components/Dialog/NonGroupingAddDialog";
import { Icon, Icons } from "../components/Icon";
import { EXCEED_GROUPING_ADD } from "../constants/ErrorMessage";
import { ADD_VALUE_ON, TABLE_ABLE } from "../constants/Features";
import { Line } from "../types/extractorResult";
import { envVarStore } from "../utils/mobx/EnvVarStore";
import { parsingTableStore } from "../utils/mobx/ParsingTableStore";
import { resultInteractionStore } from "../utils/mobx/ResultInteractionStore";
import { staticLabelStore } from "../utils/mobx/StaticLabelStore";
import { boxChangedFocusMover } from "../utils/resultScrollHelper";
import { KeyFormatter, ValueFormatter } from "../utils/stringUtil";
import { cn } from "../utils/style";

interface ParsingResultTableProps {
  disabled?: boolean;
}

function shouldKeyBeVisible(idx: number, lineGroups: any[]) {
  if (idx === 0) return true;
  return lineGroups[idx - 1].key !== lineGroups[idx].key;
}

const ParsingResultTable: FC<ParsingResultTableProps> = ({
  disabled = false,
}) => {
  const { t } = useTranslation();
  const {
    setAdditionalFocusIndexes,
    additionalFocusIndexes,
    hoveredBoxIds,
    isGrouptHovered,
    setHoveredBoxIds,
    selectedBoxIds,
    setSelectedBoxIds,
    focusIndex: curIndex,
    setFocusIndex: setIndex,
    setFocusEnable,
    isGrouptSelected,
  } = resultInteractionStore;
  const {
    filteredIndex2orgIndex,
    filteredLineGroups: lineGroups,
    visibleGroupKeys: groupKeys,
    filteredSortedGroups: sortedGroups,
    sortedGroups: orgSortedGroups,
    values,
    orgValues,
    isChanged,
    setTdRef,
    setInputRef,
    inputValueUpdate,
    delLineGroups,
    delSortedGroups,
    addSortedGroups,
    confidenceThreshold,
    lineModalOn,
    setLineModalOn,
    orgLargestId,
    undo,
  } = parsingTableStore;
  const [isRowClicked, setIsRowClicked] = useState(false);

  const [errorOn, setErrorOn] = useState<boolean>(false);
  const [errorType, setErrorType] = useState<number>(1);
  const [errorReloadFunction, setErrorReloadFunction] = useState<() => void>(
    () => {},
  );

  const trRef = useRef<HTMLTableRowElement[]>([]);
  const tdRef = useRef<HTMLTableCellElement[]>([]);
  const inputRefs = useRef<(HTMLInputElement | HTMLTextAreaElement)[]>([]);
  const divRef = useRef<HTMLDivElement>(null);

  const [td1Width, setTd1Wdith] = useState<number>(0);
  const [td2Width, setTd2Width] = useState<number>(0);

  const [blockType, _setBlockType] = useState<string>("center");

  const [hoverIndex, setHoverIndex] = useState<number>(-1);
  // const [hoverTableIndex, setHoverTableIndex] = useState<number>(-1);

  const [isTransposed, setIsTransposed] = useState<boolean>(false);
  const [isDelBtnHover, setIsDelBtnHover] = useState<boolean>(false);
  const { staticLabels } = staticLabelStore;

  useEffect(() => {
    const { initialTransposed } = envVarStore;
    const isTransposed = initialTransposed === "true";
    setIsTransposed(isTransposed);
  }, []);

  useEffect(() => {
    // 이미지뷰에서 박스가 클릭되었을때만 자동 스크롤
    if (isRowClicked) {
      setIsRowClicked(false);
      return;
    }

    if (selectedBoxIds.length) {
      boxChangedFocusMover(selectedBoxIds, "center");
      lineGroups.forEach((lineGroup, idx) => {
        if (selectedBoxIds.includes(String(lineGroup.id))) {
          setIndex(idx);
          return;
        }
      });
      sortedGroups.forEach((group, groupIdx) => {
        group.properties
          .filter(property => property.type === "content")
          .forEach((property, idx) => {
            if (selectedBoxIds.includes(String(property.id))) {
              setIndex(lineGroups.length + groupKeys.length * groupIdx + idx);
              return;
            }
          });
      });
    }
  }, [selectedBoxIds]);

  const autoResize = (textarea: HTMLTextAreaElement) => {
    // 스크롤 높이를 정확히 계산하기 위해 높이를 초기화
    textarea.style.height = "0px";

    // 스크롤 높이에 따라 높이 조절 (최소 높이 설정을 위해 Math.max 사용)
    const newHeight = Math.max(20, textarea.scrollHeight);
    textarea.style.height = newHeight + "px";
  };

  function showRollbackToast(message: string, undoAction: () => void) {
    toast({
      title: "Do you want to rollback?",
      description: message,
      action: (
        <ToastAction
          altText="Go to undo"
          onClick={() => {
            undoAction();
          }}
        >
          {t("actions.undo")}
        </ToastAction>
      ),
    });
  }

  // useEffect(() => {
  //   let maxTd = tdWidth;
  //   for (let i = 0; i <= lineGroups.length; i += 1) {
  //     if (trRef.current[i]) {
  //       maxTd = Math.max(
  //         maxTd,
  //         parseInt(
  //           window.getComputedStyle(tdRef.current[i]).width.replace("px", ""),
  //         ),
  //       );
  //     }
  //   }

  //   if (trRef.current[0] && tdWidth !== maxTd) {
  //     setTdWdith(maxTd + 2);
  //   }
  //   for (let i = 0; i <= sortedGroups.length * groupKeys.length; i += 1) {
  //     if (inputRefs.current[lineGroups.length + i]) {
  //       autoResize(
  //         inputRefs.current[lineGroups.length + i] as HTMLTextAreaElement,
  //       );
  //     }
  //   }
  // }, [
  //   trRef,
  //   tdRef,
  //   sortedGroups,
  //   tdWidth,
  //   lineGroups,
  //   svgWidth,
  //   svgHeight,
  //   groupKeys.length,
  // ]);

  useEffect(() => {
    const updateWidths = () => {
      let maxTd1 = 0;
      for (let i = 0; i <= lineGroups.length; i += 1) {
        if (trRef.current[i]) {
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          if (context) {
            context.font = "14px Noto Sans KR"; // 폰트 설정
            const width = context.measureText(lineGroups[i].key).width;
            maxTd1 = Math.max(maxTd1, width);
          }
        }
      }
      maxTd1 += 20;
      setTd1Wdith(maxTd1);

      let maxTd2 = 0;
      for (let i = 0; i <= values.length; i += 1) {
        if (trRef.current[i]) {
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          if (context) {
            context.font = "14px Noto Sans KR"; // 폰트 설정
            const width = context.measureText(values[i]).width;
            maxTd2 = Math.max(maxTd2, width);
          }
        }
      }
      maxTd2 += 20;

      if (divRef.current) {
        maxTd2 = Math.max(maxTd2, divRef.current.offsetWidth - maxTd1 - 40);
      }

      setTd2Width(maxTd2);
    };

    updateWidths();
    window.addEventListener("resize", updateWidths);

    return () => {
      window.removeEventListener("resize", updateWidths);
    };
  }, [divRef, td2Width, td1Width, values, lineGroups, inputRefs]);

  function getFilterdProperties(groupIdx: number) {
    return sortedGroups[groupIdx].properties
      .filter(property => property.type === "content")
      .filter(property => groupKeys.includes(property.key));
  }

  function getGroupingTable() {
    const targetArray1 = !isTransposed
      ? Array.from({ length: sortedGroups.length })
      : Array.from({ length: groupKeys.length });
    const targetArray2 = !isTransposed
      ? Array.from({ length: groupKeys.length })
      : Array.from({ length: sortedGroups.length });

    const groupingTable = [];
    for (let idx1 = 0; idx1 < targetArray1.length; idx1++) {
      const row = [];
      if (isTransposed) {
        row.push(
          <th
            key={`gh${idx1}`}
            id={`th-${idx1}`}
            scope="col"
            className={cn({
              "body w-full whitespace-nowrap break-words p-2 text-left text-muted-dark":
                true,
            })}
          >
            {KeyFormatter(groupKeys[idx1].split("/")[1])}
          </th>,
        );
      }
      for (let idx2 = 0; idx2 < targetArray2.length; idx2++) {
        const groupIdx = !isTransposed ? idx1 : idx2;
        const propIdx = !isTransposed ? idx2 : idx1;

        let maxWidth = isTransposed ? 100 : 10;

        for (let _idx1 = 0; _idx1 < targetArray1.length; _idx1++) {
          const groupIdx = !isTransposed ? _idx1 : idx2;
          const propIdx = !isTransposed ? idx2 : _idx1;
          const value = getValue(groupIdx, propIdx);

          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          if (context) {
            context.font = "14px Noto Sans KR"; // 폰트 설정
            const width = context.measureText(value).width;
            maxWidth = Math.max(maxWidth, width);
          }
        }

        row.push(
          <td
            key={`g${groupIdx}p${propIdx}`}
            id={`td-${lineGroups.length + groupKeys.length * groupIdx + propIdx}`}
            onClick={e => handleCellClick(e, groupIdx, propIdx)}
            onMouseEnter={() => {
              handleMouseEnter(groupIdx);
            }}
            onMouseLeave={handleMouseLeave}
            ref={el => setGroupingTdRef(el, groupIdx, propIdx)}
            className={cn(getCellClassNames(groupIdx, propIdx))}
          >
            <div className="relative">
              <div className="relative flex px-1">
                <input
                  style={{
                    width: maxWidth + 20,
                  }}
                  ref={el => setGroupingInputRef(el, groupIdx, propIdx)}
                  id={`g input-${groupIdx * groupKeys.length + propIdx}`}
                  className={cn(getInputClassNames(groupIdx, propIdx))}
                  value={getValue(groupIdx, propIdx)}
                  placeholder="N/A"
                  onClick={() => handleInputClick(groupIdx, propIdx)}
                  disabled={!TABLE_ABLE}
                  onKeyDown={event =>
                    handleKeyPress(
                      event,
                      lineGroups.length + groupKeys.length * groupIdx + propIdx,
                      tdRef,
                      inputRefs,
                    )
                  }
                  onChange={event =>
                    handleInputChange(event, groupIdx, propIdx)
                  }
                />
                {ADD_VALUE_ON && idx1 === 0 && isTransposed && (
                  <div
                    className={cn({
                      "absolute left-1/2 top-[-15px] z-10 -translate-x-1/2 transform":
                        true,
                      invisible: lineGroups.length + idx2 !== hoverIndex,
                    })}
                    key={"gg del transposed " + idx2}
                  >
                    <div
                      className={cn({
                        "flex h-[28px] w-[28px] items-center justify-center rounded-lg border border-border bg-white align-middle":
                          true,
                        "opacity-100": isDelBtnHover,
                        "opacity-50": !isDelBtnHover,
                      })}
                      onMouseEnter={() => {
                        setIsDelBtnHover(true);
                      }}
                      onMouseLeave={() => {
                        setIsDelBtnHover(false);
                      }}
                      onClick={() => {
                        delSortedGroups(idx2);
                        setHoverIndex(-1);
                        setHoveredBoxIds([]);
                        setIndex(-1);
                        showRollbackToast(
                          isTransposed ? "Column removed" : "Row removed",
                          undo,
                        );
                      }}
                      key={"g del transposed " + idx2}
                    >
                      <Icon className="py-1 opacity-100" source={Icons.Minus} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </td>,
        );
      }
      groupingTable.push(
        <tr
          key={`g${idx1}`}
          className="divide mx-0.5 my-0.5 h-9 w-full divide-x break-words"
        >
          {row}
        </tr>,
      );
    }
    return groupingTable;
  }

  function handleCellClick(
    e: React.MouseEvent<HTMLTableCellElement>,
    groupIdx: number,
    idx: number,
  ) {
    const group = sortedGroups[groupIdx];
    e.stopPropagation();
    const isGroupAdded = group.id < 0 || group.id > orgLargestId;
    setFocusEnable(!isGroupAdded);

    setIsRowClicked(true);
    setSelectedBoxIds([
      String(group.id),
      ...group.properties.map(property => String(property.id)),
    ]);
    setHoverIndex(-1);
    setHoveredBoxIds([]);
    setAdditionalFocusIndexes([]);
    setIndex(lineGroups.length + groupKeys.length * groupIdx + idx);
  }

  function handleMouseEnter(groupIdx: number) {
    const group = sortedGroups[groupIdx];
    setHoverIndex(lineGroups.length + groupIdx);
    setHoveredBoxIds([
      String(group.id),
      ...group.properties.map(property => String(property.id)),
    ]);
  }

  function handleMouseLeave() {
    setHoverIndex(-1);
    setHoveredBoxIds([]);
  }

  function setGroupingTdRef(
    el: HTMLTableCellElement | null,
    groupIdx: number,
    propIdx: number,
  ) {
    if (el) {
      tdRef.current[lineGroups.length + groupKeys.length * groupIdx + propIdx] =
        el;
      setTdRef(tdRef);
    }
  }

  function setGroupingInputRef(
    el: HTMLInputElement | null,
    groupIdx: number,
    propIdx: number,
  ) {
    if (el) {
      inputRefs.current[
        lineGroups.length + groupKeys.length * groupIdx + propIdx
      ] = el;
      setInputRef(inputRefs);
    }
  }

  function isGroupConfidence(
    groupIdx: number,
    propIdx: number,
    isGreaterThanThreshold: boolean,
  ) {
    const property = getFilterdProperties(groupIdx)[propIdx];

    if (property.confidence === undefined) {
      return false;
    }

    if (property.confidence === null) {
      return false;
    }

    if (isGreaterThanThreshold) {
      return property.confidence >= confidenceThreshold;
    }

    return property.confidence < confidenceThreshold;
  }

  function getCellClassNames(groupIdx: number, propIdx: number) {
    const group = sortedGroups[groupIdx];
    const cellIndex = lineGroups.length + groupKeys.length * groupIdx + propIdx;

    const isCellSelected =
      curIndex === cellIndex && getValue(groupIdx, propIdx) !== "";
    // const property = getFilterdProperties(groupIdx)[idx];
    return {
      "w-full px-2 py-2 whitespace-nowrap break-words overflow-visible": true,
      "shadow-inline shadow-[#111827]":
        curIndex === cellIndex || additionalFocusIndexes.includes(cellIndex),
      "text-left": getFilterdProperties(groupIdx)
        // eslint-disable-next-line no-unexpected-multiline
        [propIdx].key.split(".")
        .includes("product_name"),
      // 텍스트 색상
      "text-black":
        isGroupConfidence(groupIdx, propIdx, true) && !isChanged(cellIndex),
      "text-accent-green": isChanged(cellIndex),
      "text-accent-red":
        isGroupConfidence(groupIdx, propIdx, false) && !isChanged(cellIndex),
      // 배경 색상
      "bg-accent-blue-background/50":
        !disabled && (isGrouptHovered(group) || isGrouptSelected(group)),
      "bg-white":
        disabled || (!isGrouptHovered(group) && !isGrouptSelected(group)),
      // 커서 & 테두리
      "cursor-text": TABLE_ABLE,
      "outline-black outline rounded": isCellSelected,
    };
  }

  function getInputClassNames(groupIdx: number, idx: number) {
    return {
      "h-[20px] whitespace-nowrap overflow-visible": true,
      "bg-transparent resize-none focus:outline-none": true,
      "placeholder placeholder:text-green-400 placeholder:text-opacity-40":
        isChanged(lineGroups.length + groupKeys.length * groupIdx + idx),
      "placeholder placeholder:text-gray-200 placeholder:text-opacity-80":
        !isChanged(lineGroups.length + groupKeys.length * groupIdx + idx),
      "pointer-events-none": !TABLE_ABLE,
    };
  }

  function getValue(groupIdx: number, idx: number) {
    return ValueFormatter(
      values[
        filteredIndex2orgIndex[
          lineGroups.length + groupKeys.length * groupIdx + idx
        ]
      ],
    );
  }

  function handleInputClick(groupIdx: number, idx: number) {
    if (!TABLE_ABLE) {
      inputRefs.current[
        lineGroups.length + groupKeys.length * groupIdx + idx
      ].click();
    }
    // if (additionalFocusIndexes.length > 0) {
    //   inputRefs.current[
    //     lineGroups.length + groupKeys.length * groupIdx + idx
    //   ].blur();
    // }
  }

  function handleInputChange(
    event: React.ChangeEvent<HTMLInputElement>,
    groupIdx: number,
    idx: number,
  ) {
    if (TABLE_ABLE && !lineModalOn) {
      inputValueUpdate(
        filteredIndex2orgIndex[
          lineGroups.length + groupKeys.length * groupIdx + idx
        ],
        event.target.value,
      );
      autoResize(
        inputRefs.current[
          lineGroups.length + groupKeys.length * groupIdx + idx
        ] as HTMLTextAreaElement,
      );
    }
  }

  function deleteLineGroupButton(lineGroup: Line, idx: number) {
    return (
      <div
        className={`self-center pl-2`}
        key={"l del " + idx}
        onMouseEnter={() => {
          if (curIndex === -1) {
            setHoveredBoxIds([String(lineGroup.id)]);
            setHoverIndex(idx);
          }
        }}
        onMouseLeave={() => {
          if (curIndex === -1) {
            setHoveredBoxIds([]);
            setHoverIndex(-1);
          }
        }}
      >
        <Button
          variant="outline"
          size="icon"
          className="size-8"
          onClick={() => {
            delLineGroups(idx);
            setHoverIndex(-1);
            setHoveredBoxIds([]);
            setIndex(-1);
            showRollbackToast(t("messages.field_removed"), undo);
          }}
          key={"linegroup-delete" + idx}
        >
          <Icon className="text-muted-dark" source={Icons.Minus} />
        </Button>
      </div>
    );
  }

  return (
    <>
      <ErrorDialog
        open={errorOn}
        setOpen={setErrorOn}
        errorType={errorType}
        errorReloadFunction={errorReloadFunction}
      />
      <NonGroupingAddDialog isOpen={lineModalOn} setIsOpen={setLineModalOn} />
      <div className="flex flex-col gap-y-1.5">
        {lineGroups.length > 0 && (
          <>
            {ADD_VALUE_ON && (
              <div className="flex w-full items-center justify-end">
                <Button
                  variant="ghost"
                  className="flex gap-1.5 text-muted-dark"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    setLineModalOn(true);
                  }}
                >
                  <Icon source={Icons.Add} />
                  <div>{t(`actions.add_field`)}</div>
                </Button>
              </div>
            )}
            <div
              className="body flex min-w-full flex-col items-center overflow-auto rounded-lg bg-background p-4"
              ref={divRef}
            >
              <table className="flex w-full border-collapse flex-col">
                <tbody className="flex w-full flex-col">
                  {lineGroups.map((lineGroup, idx) => {
                    const linecontent = values[filteredIndex2orgIndex[idx]];
                    const originContent =
                      orgValues[filteredIndex2orgIndex[idx]];

                    const isSelected =
                      curIndex === idx ||
                      (curIndex === -1 &&
                        selectedBoxIds.includes(String(lineGroup.id)));
                    const isHovered =
                      hoverIndex === idx ||
                      (hoverIndex === -1 &&
                        hoveredBoxIds.includes(String(lineGroup.id)));

                    if (!lineGroup) return null;
                    return (
                      <tr
                        ref={el => {
                          trRef.current[idx] = el as HTMLTableRowElement;
                        }}
                        className={cn({
                          "divide flex w-full divide-x break-words border-b text-left last:border-b-0":
                            true,
                        })}
                        id={`row-${lineGroup.id}`}
                        key={`${lineGroup.key + "idx:" + idx}`}
                        onMouseEnter={() => {
                          setHoveredBoxIds([String(lineGroup.id)]);
                          setHoverIndex(idx);
                        }}
                        onMouseLeave={() => {
                          setHoveredBoxIds([]);
                          setHoverIndex(-1);
                        }}
                        onClick={e => {
                          e.stopPropagation();
                          setIsRowClicked(true);
                          if (lineGroup.id < 0 || lineGroup.id > orgLargestId) {
                            setFocusEnable(false);
                          } else {
                            setFocusEnable(true);
                          }
                          if (curIndex !== -1 && (e.ctrlKey || e.metaKey)) {
                            if (additionalFocusIndexes.length === 0) {
                              (
                                document.activeElement as
                                  | HTMLInputElement
                                  | HTMLTextAreaElement
                              ).blur();
                            }
                            setAdditionalFocusIndexes([
                              ...additionalFocusIndexes,
                              idx,
                            ]);
                          } else {
                            // 박스가 있을때만
                            if (
                              lineGroup.boundingBoxes &&
                              lineGroup.boundingBoxes.length > 0
                            ) {
                              setSelectedBoxIds([String(lineGroup.id)]);
                              boxChangedFocusMover(
                                [String(lineGroup.id)],
                                blockType,
                                "instant",
                                idx,
                              );
                            } else {
                              setSelectedBoxIds([]);
                            }
                            setIndex(idx);
                            setHoveredBoxIds([]);
                            setHoverIndex(-1);
                            setAdditionalFocusIndexes([]);
                          }
                        }}
                      >
                        <td
                          className={cn({
                            "flex-1 p-2": true,
                            "bg-accent-blue-background/50":
                              !disabled && (isSelected || isHovered),
                          })}
                          ref={el => {
                            tdRef.current[idx] = el as HTMLTableCellElement;
                            setTdRef(tdRef);
                          }}
                        >
                          <div
                            className={cn({
                              "text-muted-dark": true,
                              invisible: !shouldKeyBeVisible(idx, lineGroups),
                            })}
                          >
                            {KeyFormatter(lineGroup.key)}
                          </div>
                        </td>
                        <td
                          className={cn({
                            "flex flex-1": true,
                            "bg-accent-blue-background/50":
                              isSelected || isHovered,
                            "rounded outline outline-1 outline-accent-blue":
                              isSelected,
                            "bg-transparent":
                              disabled || (!isSelected && !isHovered),
                            "shadow-inline shadow-[#111827]":
                              idx === curIndex ||
                              additionalFocusIndexes.includes(idx),
                            "text-accent-green": linecontent !== originContent,
                            "text-accent-red":
                              lineGroup.confidence != null &&
                              lineGroup.confidence < confidenceThreshold &&
                              linecontent === originContent,
                          })}
                          id={`td-${idx}`}
                        >
                          <textarea
                            ref={el => {
                              inputRefs.current[idx] =
                                el as HTMLTextAreaElement;
                              // 초기 렌더링 시 높이 자동 조절
                              if (el) {
                                autoResize(el as HTMLTextAreaElement);
                              }
                            }}
                            className={cn({
                              "w-full resize-none bg-transparent p-2 focus:outline-none":
                                true,
                              "placeholder placeholder:text-accent-green/40":
                                linecontent !== originContent,
                              "placeholder placeholder:text-muted-dark/40":
                                linecontent === originContent,
                              "pointer-events-none": !TABLE_ABLE,
                            })}
                            id={"input-" + idx}
                            disabled={!TABLE_ABLE}
                            value={ValueFormatter(linecontent)}
                            placeholder="N/A"
                            onClick={() => {
                              if (!TABLE_ABLE) {
                                tdRef.current[idx].click();
                              }

                              if (additionalFocusIndexes.length > 0) {
                                // 다중 셀 선택시에는 인풋박스로 포커스되면 안된다.
                                inputRefs.current[idx].blur();
                              }
                            }}
                            onKeyDown={event =>
                              handleKeyPress(event, idx, tdRef, inputRefs)
                            }
                            onChange={event => {
                              if (TABLE_ABLE && !lineModalOn) {
                                inputValueUpdate(
                                  filteredIndex2orgIndex[idx],
                                  event.target.value,
                                );

                                autoResize(
                                  inputRefs.current[idx] as HTMLTextAreaElement,
                                );
                              }
                            }}
                          ></textarea>
                        </td>

                        {ADD_VALUE_ON && (
                          <>{deleteLineGroupButton(lineGroup, idx)}</>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Transpose Table */}
        {sortedGroups.length > 0 ? (
          <>
            {/* <div className="mt-4 flex h-[28px] w-full flex-row items-center justify-between pl-1 pt-4"> */}
            <div className="flex w-full items-center justify-between">
              <div className="flex">
                {staticLabels?.KIE?.Heading?.GroupResult[0]}
              </div>
              <div className="flex">
                <Button
                  variant="ghost"
                  className="flex items-center gap-1.5 text-muted-dark"
                  onClick={() => {
                    setIsTransposed(!isTransposed);
                    setTimeout(() => {
                      document.getElementById("th-0")?.scrollIntoView(false);
                    }, 0);
                  }}
                >
                  <Icon source={Icons.Transpose} />
                  <span>{t(`actions.transpose`)}</span>
                </Button>
                {ADD_VALUE_ON && (
                  <Button
                    variant="ghost"
                    className="flex items-center gap-1.5 text-muted-dark"
                    onClick={() => {
                      if (orgSortedGroups.length < 100) {
                        addSortedGroups(
                          curIndex === -1 || curIndex < lineGroups.length
                            ? sortedGroups.length
                            : Math.ceil(
                                (curIndex + 1 - lineGroups.length) /
                                  groupKeys.length,
                              ),
                        );
                        setFocusEnable(false);
                        setIndex(
                          lineGroups.length +
                            groupKeys.length * sortedGroups.length,
                        );

                        setTimeout(() => {
                          boxChangedFocusMover(
                            [],
                            "center",
                            "instant",
                            lineGroups.length +
                              groupKeys.length * sortedGroups.length,
                          );
                        }, 0);
                      } else {
                        setErrorOn(true);
                        setErrorType(EXCEED_GROUPING_ADD);
                        setErrorReloadFunction(() => () => {
                          setErrorOn(false);
                        });
                      }
                    }}
                  >
                    <Icon source={Icons.Add} />
                    <span>
                      {isTransposed
                        ? t(`actions.add_column`)
                        : t(`actions.add_row`)}
                    </span>
                  </Button>
                )}
              </div>
            </div>
            <div className="body w-full rounded-lg bg-white">
              <div className="flex flex-auto overflow-auto p-4 align-middle">
                <table className="divide min-w-full flex-auto table-fixed border-collapse divide-y">
                  {!isTransposed && (
                    <thead className="bg-white">
                      <tr className="divide divide-x">
                        {groupKeys.map((key, colIdx) => {
                          // const display = (receiptKeys as Keys)[key.split("/")[1]];
                          const display = key.split("/")[1];
                          return (
                            <th
                              key={colIdx}
                              id={`th-${colIdx}`}
                              scope="col"
                              className={cn({
                                "w-full whitespace-nowrap break-words border-black/5 px-2 py-2 text-left font-normal text-gray-400":
                                  true,
                              })}
                            >
                              {KeyFormatter(display)}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                  )}
                  <tbody className="divide divide-y">
                    {sortedGroups.length > 0 && getGroupingTable()}
                  </tbody>
                </table>
                <div className="absolute right-[44px] flex-col">
                  <div className="h-[34px] w-1"></div>
                  {ADD_VALUE_ON &&
                    !isTransposed &&
                    sortedGroups.map((group, idx) => (
                      <div
                        className={cn({
                          "flex h-9 items-center align-middle": true,
                          invisible: lineGroups.length + idx !== hoverIndex,
                        })}
                        key={"gg del " + idx}
                      >
                        <div
                          className={cn({
                            "flex h-[28px] w-[28px] items-center justify-center rounded-lg border border-border bg-background align-middle":
                              true,
                            "opacity-100": isDelBtnHover,
                            "opacity-50": !isDelBtnHover,
                          })}
                          onMouseEnter={() => {
                            handleMouseEnter(idx);
                            setIsDelBtnHover(true);
                          }}
                          onMouseLeave={() => {
                            handleMouseLeave();
                            setIsDelBtnHover(false);
                          }}
                          onClick={() => {
                            delSortedGroups(idx);
                            setHoverIndex(-1);
                            setHoveredBoxIds([]);
                            setIndex(-1);
                            showRollbackToast("Row removed", undo);
                          }}
                          key={"g del " + idx}
                        >
                          <Icon
                            className="py-1 opacity-100"
                            source={Icons.Minus}
                          />
                        </div>
                      </div>
                    ))}
                </div>
              </div>
              {ADD_VALUE_ON && sortedGroups.length === 0 && (
                <div className="flex w-full items-center justify-center pb-8 pt-4 text-muted-dark">
                  No field
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>
    </>
  );
};

export default observer(ParsingResultTable);
